import React, { FC } from 'react';
import { Carousel, Tooltip } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { addPromoCode } from 'app/store/reducers/cart.reducer';
import { setUiAuthModal } from 'app/store/reducers/ui.reducer';
// Apply band SVGs (Figma Frame 2359): orange "Apply offer" vs white "Applied to your bag!" state.
import applyOfferFrame from 'app/assets/images/applyOfferBand.svg';
import appliedOfferFrame from 'app/assets/images/appliedOfferBand.svg';
import { EAuthModals } from 'entities/UI/UI.models';
import { parkPromoCode } from 'entities/Promo/PendingPromoCode.hooks';

export const AnnouncementsPanel: FC = () => {
  const dispatch = useAppDispatch();
  const { activeAnnouncements } = useAppSelector((state) => state.announcement);
  const authModel = useAppSelector((state) => state.auth.authModel);
  const cartModel = useAppSelector((state) => state.cart.cartModel);
  // Same field the cart's promo label reads (PromoCodeForm): `promo` = applied, `savedPromo` = saved.
  const appliedPromoCode = cartModel?.promo?.code ?? cartModel?.savedPromo?.code ?? null;

  if (!activeAnnouncements.length) {
    return null;
  }

  const handleApplyOffer = (code: string) => () => {
    // Guests can't have a cart — park the code (applied after auth by useApplyPendingPromoCode)
    // and prompt login/register with the existing modal.
    if (!authModel) {
      parkPromoCode(code);
      dispatch(setUiAuthModal({ isVisible: true, type: EAuthModals.Type }));
      return;
    }

    // Reuses the cart promo-code pipeline; criteria-not-met surfaces in the cart's savedPromo label.
    dispatch(addPromoCode({ code }));
  };

  const slides = activeAnnouncements.map((item) => {
    // react-router <Link> turns an absolute URL into an in-app route (/https://…);
    // render a plain <a> (same tab, per spec) for external hrefs.
    const isExternalHref = /^https?:\/\//i.test(item.href || '');

    const cardInner = (
      <>
        <div className="announcements-panel-card-body-text">
          <div className="announcements-panel-card-body-title">{item.name}</div>
          <div className="announcements-panel-card-body-subtitle">{item.description}</div>
        </div>
        {item.image && <img className="announcements-panel-card-body-image" src={`/api/images/${item.image}`} alt={item.name} />}
        <Tooltip title="Terms & Conditions applied">
          <span className="announcements-panel-info">
            <InfoCircleFilled />
          </span>
        </Tooltip>
      </>
    );

    return (
      <div key={item.id} className="announcements-panel-slide">
        {/* Card = brown body (Figma Frame 2248) + optional orange Apply band (Frame 2359). */}
        <div className="announcements-panel-card">
          {/* No href → plain div, not an empty <Link>: a real <a> tag picks up global link styles
              (underline) and a pointer cursor on non-clickable cards. */}
          {!item.href ? (
            <div className="announcements-panel-card-body">{cardInner}</div>
          ) : isExternalHref ? (
            <a className="announcements-panel-card-body" href={item.href}>
              {cardInner}
            </a>
          ) : (
            <Link className="announcements-panel-card-body" to={item.href}>
              {cardInner}
            </Link>
          )}
          {item.promoCode &&
            (() => {
              const isOfferApplied = item.promoCode === appliedPromoCode;
              return (
                <button
                  type="button"
                  className={`announcements-panel-apply${isOfferApplied ? ' announcements-panel-apply_applied' : ''}`}
                  style={{ backgroundImage: `url(${isOfferApplied ? appliedOfferFrame : applyOfferFrame})` }}
                  onClick={isOfferApplied ? undefined : handleApplyOffer(item.promoCode)}
                >
                  {isOfferApplied ? 'Applied to your bag!' : 'Apply offer'}
                </button>
              );
            })()}
        </div>
      </div>
    );
  });

  return (
    <div className="announcements-panel">
      <div className="announcements-panel-container">
        {/* One announcement = plain promo, no Carousel: antd Carousel still shows dots + autoplay
            animation with a single slide, which reads as a pointless spinning strip. */}
        {activeAnnouncements.length > 1 ? (
          <Carousel autoplay autoplaySpeed={10000} dots>
            {slides}
          </Carousel>
        ) : (
          slides[0]
        )}
      </div>
    </div>
  );
};
